import { type CSSProperties, memo } from 'react'

import { kebabCase } from 'lodash'
import type { OpenAPIV3_1 as OpenApi3 } from 'openapi-types'

import { useSize } from '@subaio/components'

import { getNavigationId } from 'lib/getNavigationId'

import { Heading } from 'components/Heading'

import { Container, Content, Method } from '../Common'
import { Header } from './Header'
import { Path } from './Path'
import { Request } from './Request'
import { Response } from './Response'

type Props = {
	title?: string
	operationId: string
	path: string
	method: string
	summary?: string
	description?: string
	responses: OpenApi3.ResponsesObject
	documentParameters: Record<string, OpenApi3.ParameterObject | OpenApi3.ReferenceObject> | undefined
	definitions: Record<string, OpenApi3.SchemaObject | OpenApi3.ReferenceObject> | undefined
	securityDefinitions: Record<string, OpenApi3.SecuritySchemeObject | OpenApi3.ReferenceObject> | undefined
	parameters: Array<OpenApi3.ReferenceObject | OpenApi3.ParameterObject> | undefined
	security: Array<Record<string, Array<unknown>>> | undefined
	requestBody: OpenApi3.RequestBodyObject | OpenApi3.ReferenceObject | undefined

	style?: CSSProperties
	className?: string
}

export const Endpoint = memo((props: Props) => {
	const { title, operationId, path, method, summary, responses, style, className } = props

	const [headerRef, { height: headerHeight = 109 }] = useSize()

	const headerId = `${getNavigationId(kebabCase(operationId), title)}-header`

	return (
		<Container style={style} className={className}>
			<Content
				prefix={title}
				title={summary}
				label={kebabCase(operationId)}
				level={2}
				data-sticky-header-id={headerId}
				renderHeading={(props) => (
					<Header ref={headerRef} id={headerId}>
						<Heading {...props} />
						<Method method={method} /> <Path>{path}</Path>
					</Header>
				)}
			>
				<Request {...props} linkPrefix={`${title}-${kebabCase(operationId)}`} stickyOffset={headerHeight - 32} />
				{Object.entries(responses).map(([statusCode, response], index) => (
					<Response
						{...props}
						key={index}
						statusCode={statusCode}
						response={response}
						linkPrefix={`${title}-${kebabCase(operationId)}`}
						stickyOffset={headerHeight - 32}
					/>
				))}
			</Content>
		</Container>
	)
})
